import { ICON_PROPS, formatRelative, postToHost, shortDate, tierColor } from './shared.js';
import { Fragment, useEffect, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Chip,
  Code,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid,
  Group,
  List,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NativeSelect,
  NavLink,
  NumberInput,
  Paper,
  PasswordInput,
  Progress,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArchive,
  IconArrowBackUp,
  IconBell,
  IconBolt,
  IconBrain,
  IconBriefcase,
  IconCalendar,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCommand,
  IconDeviceMobile,
  IconDownload,
  IconEdit,
  IconFileText,
  IconFlag,
  IconFolder,
  IconGavel,
  IconHistory,
  IconMail,
  IconMailOff,
  IconMessageQuestion,
  IconMoonStars,
  IconNews,
  IconPaperclip,
  IconPencil,
  IconPhone,
  IconPlus,
  IconRadar,
  IconReload,
  IconReport,
  IconSchool,
  IconSearch,
  IconSend,
  IconSettings,
  IconShare,
  IconShield,
  IconSparkles,
  IconStar,
  IconStarFilled,
  IconTarget,
  IconTrash,
  IconUser,
  IconUserPlus,
  IconUsersGroup,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import { api, getStoredToken } from '../../api.js';
// Round 48 follow-up (2026-07-15): AddinPage.tsx monolith split — cases, rules, audit panels,
// moved VERBATIM. Layering: shared ← shared-components ← panels ← AddinPage.

export function CasesPanel(): React.JSX.Element {
  const [cases, setCases] = useState<Awaited<ReturnType<typeof api.addinCasesList>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [openCase, setOpenCase] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSummary, setNewSummary] = useState('');
  // Phase 6a — suggestions
  const [suggestions, setSuggestions] = useState<Awaited<ReturnType<typeof api.addinCasesSuggest>> | null>(null);
  const [sugBusy, setSugBusy] = useState(false);
  const [acceptingSug, setAcceptingSug] = useState<string | null>(null);
  // Bulk + filter (sort + status persisted across reloads)
  const [textFilter, setTextFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'closed'>(() => {
    try { return (localStorage.getItem('addin_cases_status') as 'all' | 'open' | 'closed') ?? 'all'; }
    catch { return 'all'; }
  });
  const [casesSortBy, setCasesSortBy] = useState<'updated' | 'msg_count' | 'name' | 'created'>(() => {
    try { return (localStorage.getItem('addin_cases_sortby') as 'updated' | 'msg_count' | 'name' | 'created') ?? 'updated'; }
    catch { return 'updated'; }
  });
  useEffect(() => { try { localStorage.setItem('addin_cases_status', statusFilter); } catch { /* ignore */ } }, [statusFilter]);
  useEffect(() => { try { localStorage.setItem('addin_cases_sortby', casesSortBy); } catch { /* ignore */ } }, [casesSortBy]);
  const [bulkSel, setBulkSel] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  function toggleCaseSel(id: string) {
    setBulkSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  async function bulkSetStatus(status: 'open' | 'closed') {
    if (bulkSel.size === 0) return;
    const verb = status === 'closed' ? 'Close' : 'Reopen';
    if (!confirm(`${verb} ${bulkSel.size} case${bulkSel.size === 1 ? '' : 's'}?`)) return;
    setBulkBusy(true);
    try {
      // Single batch call replaces the N parallel PATCHes.
      const r = await api.addinCasesBatch([...bulkSel], status === 'closed' ? 'close' : 'reopen');
      notifications.show({
        color: status === 'closed' ? 'gray' : 'blue',
        message: `${verb}d ${r.affected} case${r.affected === 1 ? '' : 's'}.`,
      });
      setBulkSel(new Set());
      load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : `Bulk ${verb.toLowerCase()} failed` });
    } finally {
      setBulkBusy(false);
    }
  }
  async function bulkDelete() {
    if (bulkSel.size === 0) return;
    if (!confirm(`Delete ${bulkSel.size} case${bulkSel.size === 1 ? '' : 's'}? Messages stay in Outlook; only the case + its links are removed. This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.addinCasesBatch([...bulkSel], 'delete');
      notifications.show({ color: 'orange', message: `Deleted ${r.affected} case${r.affected === 1 ? '' : 's'}.` });
      setBulkSel(new Set());
      load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Bulk delete failed' });
    } finally {
      setBulkBusy(false);
    }
  }

  async function load() {
    setBusy(true);
    try {
      setCases(await api.addinCasesList());
    } finally {
      setBusy(false);
    }
  }
  async function loadSuggestions() {
    setSugBusy(true);
    try { setSuggestions(await api.addinCasesSuggest(60)); } catch { /* silent */ }
    finally { setSugBusy(false); }
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: load on mount only
  useEffect(() => {
    load();
    loadSuggestions();
    // Search-row case badge: pop the requested case open on tab arrival.
    const pending = sessionStorage.getItem('addin_open_case');
    if (pending) {
      sessionStorage.removeItem('addin_open_case');
      setOpenCase(pending);
    }
    const onOpen = () => {
      const id = sessionStorage.getItem('addin_open_case');
      if (id) {
        sessionStorage.removeItem('addin_open_case');
        setOpenCase(id);
      }
    };
    window.addEventListener('ai-outlook:open-case', onOpen);
    return () => window.removeEventListener('ai-outlook:open-case', onOpen);
  }, []);

  // Keyboard nav: j/k navigate, Enter open, c create. Ignored when typing
  // or when a case detail is already open.
  const [casesCursor, setCasesCursor] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-bind on data change
  useEffect(() => {
    function isTyping() {
      const tag = (document.activeElement?.tagName ?? '').toLowerCase();
      return tag === 'input' || tag === 'textarea';
    }
    function onKey(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (openCase) return; // detail view open — let it handle its own keys
      if (isTyping()) return;
      if (e.key === 'c') { e.preventDefault(); setCreateOpen(true); return; }
      if (!cases || cases.cases.length === 0) return;
      const list = cases.cases;
      const idx = Math.min(casesCursor, list.length - 1);
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setCasesCursor(Math.min(list.length - 1, idx + 1));
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setCasesCursor(Math.max(0, idx - 1));
      } else if (e.key === 'Enter' && list[idx]) {
        e.preventDefault();
        setOpenCase(list[idx].id);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cases, casesCursor, openCase]);

  async function acceptSuggestion(s: NonNullable<typeof suggestions>['suggestions'][number]) {
    setAcceptingSug(s.suggestion_id);
    try {
      await api.addinCaseFromSuggestion({
        name: s.suggested_name || '(unnamed case)',
        summary: s.reason,
        priority: s.tier_top === 'P0' ? 'high' : s.tier_top === 'P1' ? 'high' : 'medium',
        message_ids: s.message_ids,
      });
      notifications.show({ color: 'green', message: `Created case from ${s.message_ids.length} message${s.message_ids.length === 1 ? '' : 's'}` });
      setSuggestions((prev) =>
        prev ? { ...prev, suggestions: prev.suggestions.filter((x) => x.suggestion_id !== s.suggestion_id) } : prev,
      );
      load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
    } finally {
      setAcceptingSug(null);
    }
  }
  function dismissSuggestion(suggestion_id: string) {
    setSuggestions((prev) =>
      prev ? { ...prev, suggestions: prev.suggestions.filter((x) => x.suggestion_id !== suggestion_id) } : prev,
    );
  }

  async function createCase() {
    if (!newName.trim()) return;
    // Duplicate detection — Levenshtein vs existing names. If close (≤2 chars
    // off + length ≥4) prompt the user to confirm or use the existing one.
    function lev(a: string, b: string): number {
      if (a === b) return 0;
      const al = a.length, bl = b.length;
      if (al === 0 || bl === 0) return Math.max(al, bl);
      const dp = Array.from({ length: al + 1 }, () => new Array<number>(bl + 1).fill(0));
      for (let i = 0; i <= al; i++) dp[i]![0] = i;
      for (let j = 0; j <= bl; j++) dp[0]![j] = j;
      for (let i = 1; i <= al; i++) {
        for (let j = 1; j <= bl; j++) {
          const cost = a[i - 1] === b[j - 1] ? 0 : 1;
          dp[i]![j] = Math.min(dp[i - 1]![j]! + 1, dp[i]![j - 1]! + 1, dp[i - 1]![j - 1]! + cost);
        }
      }
      return dp[al]![bl]!;
    }
    const candidate = newName.trim().toLowerCase();
    const close = (cases?.cases ?? []).find((c) => {
      const n = c.name.toLowerCase();
      if (n === candidate) return true;
      if (Math.min(n.length, candidate.length) < 4) return false;
      return lev(n, candidate) <= 2;
    });
    if (close && !confirm(`A case named "${close.name}" already exists. Create "${newName.trim()}" anyway?`)) return;
    await api.addinCaseCreate({ name: newName.trim(), ...(newSummary.trim() ? { summary: newSummary.trim() } : {}) });
    setNewName('');
    setNewSummary('');
    setCreateOpen(false);
    load();
  }

  if (openCase) {
    return <CaseDetail id={openCase} onBack={() => { setOpenCase(null); load(); }} />;
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Stack gap={0}>
          <Title order={4}>Cases & projects</Title>
          <Text size="xs" c="dimmed">
            Group long-running threads under a named case. Track status, summary, and related
            messages across mailboxes.
          </Text>
        </Stack>
        <Group gap="xs">
          {cases && cases.cases.length > 0 && (
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconFileText size={12} />}
              onClick={() => {
                if (!cases) return;
                // Selected-only when bulk selection is active; otherwise the
                // currently-filtered + sorted list.
                const lower = textFilter.trim().toLowerCase();
                const selectedOnly = bulkSel.size > 0;
                const visible = (selectedOnly
                  ? cases.cases.filter((c) => bulkSel.has(c.id))
                  : cases.cases.filter((c) => {
                      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
                      if (lower && !`${c.name} ${c.summary ?? ''}`.toLowerCase().includes(lower)) return false;
                      return true;
                    })
                ).sort((a, b) => {
                    if (casesSortBy === 'msg_count') return b.message_count - a.message_count;
                    if (casesSortBy === 'name') return a.name.localeCompare(b.name);
                    if (casesSortBy === 'created') return b.created_at.localeCompare(a.created_at);
                    return (b.latest_activity ?? b.updated_at).localeCompare(a.latest_activity ?? a.updated_at);
                  });
                const esc = (s: string | number | null | undefined) => {
                  if (s == null) return '';
                  const v = String(s);
                  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
                };
                const header = ['name', 'status', 'priority', 'message_count', 'latest_activity', 'created_at', 'updated_at', 'summary'].join(',');
                const lines = [
                  header,
                  ...visible.map((c) => [
                    c.name, c.status, c.priority, c.message_count, c.latest_activity,
                    c.created_at, c.updated_at, c.summary,
                  ].map(esc).join(',')),
                ];
                const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const tag = [
                  statusFilter !== 'all' ? statusFilter : null,
                  textFilter.trim() ? 'q' : null,
                ].filter(Boolean).join('-');
                a.download = `cases${tag ? `-${tag}` : ''}-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              }}
            >
              CSV
            </Button>
          )}
          <Button size="xs" variant="default" onClick={load} loading={busy}>
            Refresh
          </Button>
          <Button size="xs" onClick={() => setCreateOpen(true)}>
            + New case
          </Button>
        </Group>
      </Group>

      <Modal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New case"
        size="md"
        centered
      >
        <Stack gap="xs">
          <TextInput
            label="Case name"
            value={newName}
            onChange={(e) => setNewName(e.currentTarget.value)}
            placeholder="e.g. SMS Interconnection / TRA Submission"
            required
          />
          <Textarea
            label="Summary (optional)"
            autosize
            minRows={2}
            value={newSummary}
            onChange={(e) => setNewSummary(e.currentTarget.value)}
            placeholder="Short description of what this case is about and what's at stake"
          />
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={createCase} disabled={!newName.trim()}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Phase 6a — AI-suggested cases (no LLM call; thread clustering) */}
      {suggestions && suggestions.suggestions.length > 0 && (
        <Card padding="md" radius="md" withBorder
          style={{ borderLeft: '4px solid var(--mantine-color-violet-6)' }}>
          <Group gap="xs" mb="xs" justify="space-between">
            <Group gap="xs">
              <IconSparkles {...ICON_PROPS} />
              <Text fw={600}>Suggested cases</Text>
              <Badge color="violet">{suggestions.suggestions.length}</Badge>
            </Group>
            <Button size="compact-xs" variant="subtle" onClick={loadSuggestions} loading={sugBusy}>
              Refresh
            </Button>
          </Group>
          <Text size="xs" c="dimmed" mb="xs">
            Threads clustered from the last {suggestions.window_days} days. Accept to create a case + auto-attach the messages.
          </Text>
          <Stack gap="xs">
            {suggestions.suggestions.slice(0, 8).map((s) => (
              <Paper key={s.suggestion_id} p="xs" withBorder>
                <Group justify="space-between" wrap="nowrap" gap="xs" align="flex-start">
                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Group gap={6} wrap="nowrap">
                      <Badge size="xs" variant="dot" color={s.kind === 'cluster' ? 'violet' : 'blue'}>
                        {s.kind === 'cluster' ? 'Cluster' : 'Long thread'}
                      </Badge>
                      {s.tier_top && (
                        <Badge size="xs" color={s.tier_top === 'P0' ? 'red' : s.tier_top === 'P1' ? 'orange' : 'gray'}>
                          {s.tier_top}
                        </Badge>
                      )}
                      <Text size="sm" fw={500} lineClamp={1}>{s.suggested_name}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">{s.reason}</Text>
                    <Text size="xs" c="dimmed">
                      {s.participant_count} participant{s.participant_count === 1 ? '' : 's'} ·
                      last activity {formatRelative(s.last_at)}
                    </Text>
                  </Stack>
                  <Group gap={4} style={{ flexShrink: 0 }}>
                    <Button
                      size="compact-xs"
                      loading={acceptingSug === s.suggestion_id}
                      onClick={() => acceptSuggestion(s)}
                    >
                      Create case
                    </Button>
                    <Tooltip label="Dismiss">
                      <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => dismissSuggestion(s.suggestion_id)}>
                        <IconX {...ICON_PROPS} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Card>
      )}

      {!cases || cases.cases.length === 0 ? (
        <Card padding="xl" radius="md" withBorder>
          <Stack align="center" gap={4}>
            <Text fw={600}>No cases yet</Text>
            <Text size="sm" c="dimmed" ta="center" maw={400}>
              Cases group related emails into a single workstream you can track. Create one for
              every business deal, project, regulatory submission, or customer issue.
            </Text>
            <Button size="xs" mt="sm" onClick={() => setCreateOpen(true)}>
              Create your first case
            </Button>
          </Stack>
        </Card>
      ) : (() => {
        const lower = textFilter.trim().toLowerCase();
        const visibleCases = cases.cases
          .filter((c) => {
            if (statusFilter !== 'all' && c.status !== statusFilter) return false;
            if (lower) {
              const hay = `${c.name} ${c.summary ?? ''}`.toLowerCase();
              if (!hay.includes(lower)) return false;
            }
            return true;
          })
          .sort((a, b) => {
            if (casesSortBy === 'msg_count') return b.message_count - a.message_count;
            if (casesSortBy === 'name') return a.name.localeCompare(b.name);
            if (casesSortBy === 'created') return b.created_at.localeCompare(a.created_at);
            // 'updated' default — by latest_activity desc, fall back to updated_at
            return (b.latest_activity ?? b.updated_at).localeCompare(a.latest_activity ?? a.updated_at);
          });
        return (
        <Stack gap="xs">
          {/* Filter row + bulk action bar */}
          <Group gap="xs" wrap="nowrap">
            <TextInput
              size="xs"
              placeholder="Filter by name or summary…"
              value={textFilter}
              onChange={(e) => setTextFilter(e.currentTarget.value)}
              style={{ flex: 1 }}
              rightSection={textFilter ? (
                <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setTextFilter('')}>
                  <IconX size={10} />
                </ActionIcon>
              ) : null}
            />
            <SegmentedControl
              size="xs"
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as 'all' | 'open' | 'closed')}
              data={[
                { value: 'all', label: 'All' },
                { value: 'open', label: 'Open' },
                { value: 'closed', label: 'Closed' },
              ]}
            />
            <Select
              size="xs"
              w={140}
              value={casesSortBy}
              onChange={(v) => v && setCasesSortBy(v as 'updated' | 'msg_count' | 'name' | 'created')}
              data={[
                { value: 'updated', label: 'Latest activity' },
                { value: 'msg_count', label: 'Message count' },
                { value: 'name', label: 'Name (A→Z)' },
                { value: 'created', label: 'Newest created' },
              ]}
            />
          </Group>
          {bulkSel.size > 0 && (
            <Paper p="xs" withBorder
              style={{ background: 'var(--mantine-color-blue-0)', borderLeft: '3px solid var(--mantine-color-blue-6)' }}>
              <Group justify="space-between">
                <Text size="xs" fw={500}>{bulkSel.size} selected</Text>
                <Group gap={4}>
                  {statusFilter !== 'closed' && (
                    <Button size="compact-xs" color="gray" variant="light"
                      loading={bulkBusy} onClick={() => bulkSetStatus('closed')}>
                      Close selected
                    </Button>
                  )}
                  {statusFilter !== 'open' && (
                    <Button size="compact-xs" color="blue" variant="light"
                      loading={bulkBusy} onClick={() => bulkSetStatus('open')}>
                      Reopen selected
                    </Button>
                  )}
                  <Button size="compact-xs" variant="default" leftSection={<IconFileText size={12} />}
                    onClick={() => {
                      const rows = (cases?.cases ?? []).filter((c) => bulkSel.has(c.id));
                      const text = rows.map((c, i) => [
                        `${i + 1}. ${c.name}`,
                        `   Status: ${c.status} · Priority: ${c.priority} · ${c.message_count} msg`,
                        c.summary ? `   ${c.summary}` : '',
                      ].filter(Boolean).join('\n')).join('\n\n');
                      navigator.clipboard.writeText(text)
                        .then(() => notifications.show({ message: `Copied ${rows.length} case${rows.length === 1 ? '' : 's'}.` }))
                        .catch(() => notifications.show({ color: 'red', message: 'Copy failed' }));
                    }}>
                    Copy text
                  </Button>
                  <Button size="compact-xs" color="red" variant="light"
                    loading={bulkBusy} onClick={bulkDelete}>
                    Delete
                  </Button>
                  <Button size="compact-xs" variant="subtle" color="gray"
                    onClick={() => setBulkSel(new Set())}>Clear</Button>
                </Group>
              </Group>
            </Paper>
          )}
          {visibleCases.length === 0 && (
            <Text size="xs" c="dimmed" ta="center" py="md">No cases match this filter.</Text>
          )}
          {visibleCases.map((c, i) => (
            <Card
              key={c.id}
              padding="md"
              radius="md"
              withBorder
              style={{
                cursor: 'pointer',
                borderLeft: `4px solid var(--mantine-color-${c.status === 'open' ? 'blue' : c.status === 'closed' ? 'gray' : 'orange'}-6)`,
                background: bulkSel.has(c.id)
                  ? 'var(--mantine-color-blue-0)'
                  : i === Math.min(casesCursor, visibleCases.length - 1)
                    ? 'var(--mantine-color-gray-1)' : undefined,
                outline: i === Math.min(casesCursor, visibleCases.length - 1)
                  ? '1px solid var(--mantine-color-gray-4)' : 'none',
              }}
              onClick={() => setOpenCase(c.id)}
            >
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <Group gap="xs" wrap="nowrap" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
                  <Checkbox
                    size="xs"
                    checked={bulkSel.has(c.id)}
                    onChange={() => toggleCaseSel(c.id)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="select case for bulk"
                    mt={4}
                  />
                <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={600} size="md" lineClamp={1}>
                    {c.name}
                  </Text>
                  {c.summary && (
                    <Text size="sm" c="dimmed" lineClamp={2}>
                      {c.summary}
                    </Text>
                  )}
                  <Group gap="xs" mt={4}>
                    <Badge color={c.status === 'open' ? 'blue' : c.status === 'closed' ? 'gray' : 'orange'}>
                      {c.status}
                    </Badge>
                    <Badge variant="light">{c.priority}</Badge>
                    <Text size="xs" c="dimmed">
                      {c.message_count} email{c.message_count === 1 ? '' : 's'}
                      {c.latest_activity ? ` · last activity ${formatRelative(c.latest_activity)}` : ''}
                    </Text>
                  </Group>
                </Stack>
                </Group>
              </Group>
            </Card>
          ))}
        </Stack>
        );
      })()}
    </Stack>
  );
}

export function CaseDetail({ id, onBack }: { id: string; onBack: () => void }): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinCaseGet>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState('');
  // Multi-membership map: message_id → array of cases. Used to show
  // "also in" badges so users see overlap with other cases.
  const [multiCaseMap, setMultiCaseMap] = useState<Map<string, Array<{ case_id: string; case_name: string; status: string }>>>(new Map());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [allCases, setAllCases] = useState<Awaited<ReturnType<typeof api.addinCasesList>> | null>(null);
  const [merging, setMerging] = useState(false);
  const [mergeTarget, setMergeTarget] = useState<string | null>(null);
  // Case detail bulk-remove: per-message multi-select.
  const [bulkSel, setBulkSel] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [timelineMode, setTimelineMode] = useState(false);
  const [msgCursor, setMsgCursor] = useState<number>(-1);

  function toggleBulkSel(mid: string) {
    setBulkSel((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid); else next.add(mid);
      return next;
    });
  }
  async function bulkRemove() {
    if (bulkSel.size === 0) return;
    if (!confirm(`Remove ${bulkSel.size} message${bulkSel.size === 1 ? '' : 's'} from this case? The emails stay in your inbox; only the case link is removed.`)) return;
    setBulkBusy(true);
    try {
      const ids = [...bulkSel];
      await Promise.all(ids.map((mid) => api.addinCaseRemoveMessage(id, mid)));
      notifications.show({ message: `Removed ${ids.length} from case.` });
      setBulkSel(new Set());
      load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Bulk remove failed' });
    } finally {
      setBulkBusy(false);
    }
  }

  async function openMergeDialog() {
    setMergeTarget(null);
    setMergeOpen(true);
    try { setAllCases(await api.addinCasesList()); } catch { /* silent */ }
  }

  async function performMerge() {
    if (!mergeTarget || !data || merging) return;
    if (mergeTarget === id) {
      notifications.show({ color: 'red', message: 'Cannot merge a case into itself.' });
      return;
    }
    const message_ids = data.messages.map((m) => m.message_id);
    if (message_ids.length === 0) {
      notifications.show({ color: 'orange', message: 'No messages in this case to merge.' });
      return;
    }
    if (!confirm(`Merge ${message_ids.length} message${message_ids.length === 1 ? '' : 's'} from "${data.case.name}" into the target case, then delete this case? This cannot be undone.`)) return;
    setMerging(true);
    try {
      await api.addinCaseAddMessages(mergeTarget, message_ids);
      await api.addinCaseDelete(id);
      notifications.show({ color: 'green', message: `Merged ${message_ids.length} message${message_ids.length === 1 ? '' : 's'} and deleted this case.` });
      setMergeOpen(false);
      onBack();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Merge failed' });
    } finally {
      setMerging(false);
    }
  }

  async function load() {
    setBusy(true);
    try {
      const r = await api.addinCaseGet(id);
      setData(r);
      setSummaryDraft(r.case.summary ?? '');
    } finally {
      setBusy(false);
    }
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: load on id change
  useEffect(() => {
    load();
    // Pull all memberships once per detail view; cheap (capped at 5000).
    api.addinCasesMembership().then((r) => {
      const m = new Map<string, Array<{ case_id: string; case_name: string; status: string }>>();
      for (const x of r.memberships) {
        const arr = m.get(x.message_id) ?? [];
        arr.push({ case_id: x.case_id, case_name: x.case_name, status: x.status });
        m.set(x.message_id, arr);
      }
      setMultiCaseMap(m);
    }).catch(() => { /* silent */ });
  }, [id]);

  // j/k/Enter cursor through related messages — Enter opens current in Outlook.
  useEffect(() => {
    if (!data || data.messages.length === 0) return;
    const ordered = timelineMode
      ? [...data.messages].sort((a, b) => (a.received_at ?? '').localeCompare(b.received_at ?? ''))
      : data.messages;
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setMsgCursor((c) => Math.min(ordered.length - 1, c < 0 ? 0 : c + 1));
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setMsgCursor((c) => Math.max(0, c < 0 ? 0 : c - 1));
      } else if (e.key === 'Enter') {
        const c = msgCursor;
        const row = c >= 0 ? ordered[c] : undefined;
        if (row) {
          e.preventDefault();
          postToHost({ type: 'open_in_outlook', message_id: row.message_id });
        }
      } else if (e.key === 'x' || e.key === ' ') {
        const c = msgCursor;
        const row = c >= 0 ? ordered[c] : undefined;
        if (row) {
          e.preventDefault();
          toggleBulkSel(row.message_id);
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [data, timelineMode, msgCursor]);

  async function setStatus(s: string) {
    await api.addinCasePatch(id, { status: s });
    load();
  }
  async function setPriority(p: string) {
    await api.addinCasePatch(id, { priority: p });
    load();
  }
  async function saveSummary() {
    await api.addinCasePatch(id, { summary: summaryDraft });
    setEditingSummary(false);
    load();
  }
  async function removeMessage(mid: string) {
    if (!confirm('Remove this email from the case?')) return;
    await api.addinCaseRemoveMessage(id, mid);
    load();
  }
  async function deleteCase() {
    if (!confirm('Delete this case? Underlying emails are not deleted.')) return;
    await api.addinCaseDelete(id);
    onBack();
  }

  if (busy && !data) return <Center p="xl"><Loader /></Center>;
  if (!data) return <Text c="dimmed">Could not load case.</Text>;

  return (
    <Stack gap="sm">
      <Group justify="space-between" wrap="wrap">
        <Group gap="xs">
          <Button size="xs" variant="subtle" onClick={onBack}>
            ← Cases
          </Button>
          <Title order={4}>{data.case.name}</Title>
        </Group>
        <Group gap="xs">
          <Select
            size="xs"
            data={[
              { value: 'open', label: 'Open' },
              { value: 'waiting', label: 'Waiting' },
              { value: 'closed', label: 'Closed' },
            ]}
            value={data.case.status}
            onChange={(v) => v && setStatus(v)}
          />
          <Select
            size="xs"
            data={[
              { value: 'high', label: 'High' },
              { value: 'medium', label: 'Medium' },
              { value: 'low', label: 'Low' },
            ]}
            value={data.case.priority}
            onChange={(v) => v && setPriority(v)}
          />
          <Button size="xs" variant="default" leftSection={<IconArchive size={12} />} onClick={openMergeDialog}>
            Merge…
          </Button>
          <Button size="xs" color="red" variant="light" onClick={deleteCase}>
            Delete case
          </Button>
        </Group>
      </Group>

      <Modal
        opened={mergeOpen}
        onClose={() => setMergeOpen(false)}
        title="Merge case into another"
        size="md"
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            All {data?.messages.length ?? 0} message{(data?.messages.length ?? 0) === 1 ? '' : 's'} from
            <b> {data?.case.name}</b> will be added to the target case, then this case is deleted.
            This cannot be undone.
          </Text>
          <Select
            label="Target case"
            placeholder={allCases ? 'Pick a case…' : 'Loading…'}
            data={(allCases?.cases ?? [])
              .filter((c) => c.id !== id)
              .map((c) => ({ value: c.id, label: `${c.name} (${c.status} · ${c.message_count} msg)` }))}
            value={mergeTarget}
            onChange={setMergeTarget}
            searchable
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setMergeOpen(false)} disabled={merging}>Cancel</Button>
            <Button color="orange" onClick={performMerge} loading={merging} disabled={!mergeTarget}>
              Merge + delete this case
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Card padding="md" radius="md" withBorder>
        <Group justify="space-between" mb="xs">
          <Text fw={600} size="sm">
            Summary
          </Text>
          {!editingSummary && (
            <Button
              size="compact-xs"
              variant="subtle"
              onClick={() => setEditingSummary(true)}
            >
              Edit
            </Button>
          )}
        </Group>
        {editingSummary ? (
          <Stack gap="xs">
            <Textarea
              autosize
              minRows={3}
              value={summaryDraft}
              onChange={(e) => setSummaryDraft(e.currentTarget.value)}
            />
            <Group justify="flex-end" gap="xs">
              <Button size="xs" variant="default" onClick={() => setEditingSummary(false)}>
                Cancel
              </Button>
              <Button size="xs" onClick={saveSummary}>
                Save
              </Button>
            </Group>
          </Stack>
        ) : (
          <Text size="sm" {...(data.case.summary ? {} : { c: 'dimmed' })}>
            {data.case.summary || 'No summary yet — click Edit to add one.'}
          </Text>
        )}
      </Card>

      <Card padding="md" radius="md" withBorder>
        <Group justify="space-between" mb="xs">
          <Group gap={4}>
            <Text fw={600} size="sm">
              Related emails ({data.messages.length})
            </Text>
            {data.messages.length > 1 && (
              <Tooltip label="j/k or ↑/↓ to move, Enter to open, x or Space to select">
                <Text size="xs" c="dimmed" style={{ cursor: 'help' }}>j/k</Text>
              </Tooltip>
            )}
          </Group>
          <Group gap={4}>
            {data.messages.length > 0 && (
              <SegmentedControl
                size="xs"
                value={timelineMode ? 'timeline' : 'compact'}
                onChange={(v) => setTimelineMode(v === 'timeline')}
                data={[
                  { value: 'compact', label: 'Compact' },
                  { value: 'timeline', label: 'Timeline' },
                ]}
              />
            )}
            {timelineMode && data.messages.length > 0 && (
              <Button size="compact-xs" variant="subtle" leftSection={<IconFileText size={12} />}
                onClick={() => {
                  const ordered = [...data.messages].sort((a, b) =>
                    (a.received_at ?? '').localeCompare(b.received_at ?? ''));
                  const esc = (s: string | null | undefined) => {
                    if (s == null) return '';
                    const v = String(s);
                    return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
                  };
                  const rows = ['day,received_at,from,subject,tier,message_id'];
                  let prevDay = '';
                  for (const m of ordered) {
                    const day = (m.received_at ?? '').slice(0, 10);
                    const dayCell = day !== prevDay ? day : '';
                    prevDay = day;
                    rows.push([dayCell, m.received_at ?? '', m.from_addr ?? '',
                      m.subject ?? '', m.classification_tier ?? '', m.message_id]
                      .map(esc).join(','));
                  }
                  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  const safeName = (data.case.name || 'case').replace(/[^a-zA-Z0-9-_]+/g, '-').slice(0, 40);
                  a.download = `case-${safeName}-timeline-${new Date().toISOString().slice(0, 10)}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                }}>
                CSV
              </Button>
            )}
            {data.messages.length > 0 && (
              <Button size="compact-xs" variant="subtle" color="gray"
                onClick={() => setBulkSel(bulkSel.size === data.messages.length
                  ? new Set()
                  : new Set(data.messages.map((m) => m.message_id))
                )}>
                {bulkSel.size === data.messages.length && data.messages.length > 0 ? 'Clear all' : 'Select all'}
              </Button>
            )}
          </Group>
        </Group>
        {/* Bulk action bar — only when ≥1 selected */}
        {bulkSel.size > 0 && (
          <Paper p="xs" withBorder mb="xs"
            style={{ background: 'var(--mantine-color-red-0)', borderLeft: '3px solid var(--mantine-color-red-5)' }}>
            <Group justify="space-between">
              <Text size="xs" fw={500}>{bulkSel.size} selected</Text>
              <Group gap={4}>
                <Button size="compact-xs" color="red" variant="light"
                  loading={bulkBusy} onClick={bulkRemove}>
                  Remove from case
                </Button>
                <Button size="compact-xs" variant="subtle" color="gray"
                  onClick={() => setBulkSel(new Set())}>Clear</Button>
              </Group>
            </Group>
          </Paper>
        )}
        {data.messages.length === 0 ? (
          <Text c="dimmed" size="sm">
            No emails added yet. Open an email and click "Add to case" to attach it here.
          </Text>
        ) : (
          <Stack gap={2}>
            {(timelineMode
              ? [...data.messages].sort((a, b) => (a.received_at ?? '').localeCompare(b.received_at ?? ''))
              : data.messages
            ).map((m, idx, arr) => {
              // In timeline mode, insert a date divider when day changes.
              const day = (m.received_at ?? '').slice(0, 10);
              const prevDay = idx > 0 ? (arr[idx - 1]?.received_at ?? '').slice(0, 10) : null;
              const showDivider = timelineMode && day && day !== prevDay;
              return (
                <Fragment key={m.message_id}>
                  {showDivider && (
                    <Divider label={day} labelPosition="left" />
                  )}
              <Group
                gap="sm"
                wrap="nowrap"
                onClick={() => postToHost({ type: 'open_in_outlook', message_id: m.message_id })}
                style={{ cursor: 'pointer', padding: 4,
                  background: idx === msgCursor
                    ? 'var(--mantine-color-blue-1)'
                    : bulkSel.has(m.message_id) ? 'var(--mantine-color-red-0)' : undefined,
                  borderLeft: idx === msgCursor ? '3px solid var(--mantine-color-blue-6)' : '3px solid transparent' }}
              >
                <Checkbox
                  size="xs"
                  checked={bulkSel.has(m.message_id)}
                  onChange={() => toggleBulkSel(m.message_id)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label="select message for bulk"
                />
                {m.classification_tier && (
                  <Badge color={tierColor(m.classification_tier)} size="xs">
                    {m.classification_tier}
                  </Badge>
                )}
                <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500} lineClamp={1}>
                    {m.subject || '(no subject)'}
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {m.from_addr ?? 'unknown'}
                    {m.received_at ? ` · ${shortDate(m.received_at)}` : ''}
                  </Text>
                  {/* "Also in" strip — other cases this message belongs to */}
                  {(() => {
                    const others = (multiCaseMap.get(m.message_id) ?? []).filter((c) => c.case_id !== id);
                    if (others.length === 0) return null;
                    return (
                      <Group gap={4} mt={2}>
                        <Text size="xs" c="dimmed">Also in:</Text>
                        {others.slice(0, 3).map((c) => (
                          <Tooltip key={c.case_id} label={`${c.case_name} (${c.status})`}>
                            <Badge
                              size="xs"
                              color={c.status === 'open' ? 'blue' : c.status === 'closed' ? 'gray' : 'orange'}
                              variant="light"
                              leftSection={<IconFolder size={9} />}
                              style={{ cursor: 'pointer', maxWidth: 120 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                sessionStorage.setItem('addin_open_case', c.case_id);
                                window.dispatchEvent(new Event('ai-outlook:open-case'));
                              }}
                            >
                              <Text size="xs" lineClamp={1}>{c.case_name}</Text>
                            </Badge>
                          </Tooltip>
                        ))}
                        {others.length > 3 && (
                          <Badge size="xs" variant="dot">+{others.length - 3}</Badge>
                        )}
                      </Group>
                    );
                  })()}
                </Stack>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeMessage(m.message_id);
                  }}
                >
                  Remove
                </Button>
              </Group>
            </Fragment>
              );
            })}
          </Stack>
        )}
      </Card>
    </Stack>
  );
}

export function RulesPanel(): React.JSX.Element {
  const [rules, setRules] = useState<Awaited<ReturnType<typeof api.addinRulesList>> | null>(null);
  const [suggestions, setSuggestions] = useState<
    Awaited<ReturnType<typeof api.addinRuleSuggestions>> | null
  >(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setBusy(true);
    try {
      const [r, s] = await Promise.all([api.addinRulesList(), api.addinRuleSuggestions()]);
      setRules(r);
      setSuggestions(s);
    } finally {
      setBusy(false);
    }
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount only
  useEffect(() => {
    load();
  }, []);

  const [manualOpen, setManualOpen] = useState(false);
  const [editing, setEditing] = useState<{ id: string; name: string; when: string; then: string } | null>(null);
  // Phase 3: Safety review modal state
  const [safetyReviewFor, setSafetyReviewFor] = useState<{
    rule_id?: string;
    when: Record<string, unknown>;
    then: Record<string, unknown>;
    onConfirm: () => Promise<void>;
  } | null>(null);
  const [recentActivations, setRecentActivations] = useState<Awaited<ReturnType<typeof api.addinRuleRecentActivations>> | null>(null);

  async function loadRecentActivations() {
    try { setRecentActivations(await api.addinRuleRecentActivations()); } catch { /* silent */ }
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount only
  useEffect(() => { loadRecentActivations(); }, []);

  async function undoActivation(token: string) {
    try {
      const r = await api.addinRuleUndo(token);
      notifications.show({ color: 'orange', message: `Undone — ${r.undone} rule${r.undone === 1 ? '' : 's'} disabled.` });
      loadRecentActivations();
      load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
    }
  }

  async function approveSuggestion(s: {
    id: string;
    reason: string;
    when: Record<string, unknown>;
    then: Record<string, unknown>;
  }) {
    // Phase 3: route through safety review BEFORE creating the rule.
    setSafetyReviewFor({
      when: s.when,
      then: s.then,
      onConfirm: async () => {
        const senderHint = (s.when.sender as string | undefined) ?? 'auto-rule';
        const created = await api.addinRuleCreate({
          name: `Auto: archive from ${senderHint}`,
          when: s.when,
          then: s.then,
        });
        await api.addinRuleActivate(created.id);
        notifications.show({ color: 'green', message: 'Rule created + activated. 24h undo available.' });
        setSuggestions((prev) =>
          prev ? { ...prev, suggestions: prev.suggestions.filter((x) => x.id !== s.id) } : prev,
        );
        api.addinRulesList().then(setRules).catch(() => {});
        loadRecentActivations();
      },
    });
  }

  async function dismissSuggestion(id: string) {
    setSuggestions((prev) =>
      prev ? { ...prev, suggestions: prev.suggestions.filter((x) => x.id !== id) } : prev,
    );
  }

  async function toggleRule(id: string, enabled: boolean) {
    // Phase 3: enabling a rule routes through Safety Review. Disabling is always safe.
    if (!enabled) {
      await api.addinRulePatch(id, { enabled: false });
      load();
      return;
    }
    const rule = rules?.rules.find((r) => r.id === id);
    if (!rule) return;
    let when: Record<string, unknown> = {};
    let then: Record<string, unknown> = {};
    try { when = JSON.parse(rule.when_json); then = JSON.parse(rule.then_json); } catch { /* default */ }
    setSafetyReviewFor({
      rule_id: id,
      when,
      then,
      onConfirm: async () => {
        await api.addinRuleActivate(id);
        notifications.show({ color: 'green', message: 'Rule activated. 24h undo available.' });
        load();
        loadRecentActivations();
      },
    });
  }
  async function deleteRule(id: string) {
    if (!confirm('Delete this rule?')) return;
    await api.addinRuleDelete(id);
    load();
  }
  async function saveManualRule(name: string, whenJson: string, thenJson: string) {
    let when: Record<string, unknown>;
    let then: Record<string, unknown>;
    try { when = JSON.parse(whenJson); then = JSON.parse(thenJson); }
    catch (e) { notifications.show({ color: 'red', message: 'Invalid JSON: ' + (e instanceof Error ? e.message : '') }); return; }
    try {
      if (editing) {
        // Edits to existing rules don't change activation state — bypass safety review.
        await api.addinRulePatch(editing.id, { name, when, then });
        notifications.show({ message: 'Rule updated.' });
        setManualOpen(false);
        setEditing(null);
        load();
      } else {
        // New rules are created disabled, then routed through Safety Review for activation.
        const created = await api.addinRuleCreate({ name, when, then });
        notifications.show({ message: 'Rule created (disabled). Review safety to activate.' });
        setManualOpen(false);
        load();
        setSafetyReviewFor({
          rule_id: created.id,
          when,
          then,
          onConfirm: async () => {
            await api.addinRuleActivate(created.id);
            notifications.show({ color: 'green', message: 'Rule activated. 24h undo available.' });
            load();
            loadRecentActivations();
          },
        });
      }
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
    }
  }
  function startEdit(r: { id: string; name: string; when_json: string; then_json: string }) {
    setEditing({ id: r.id, name: r.name, when: r.when_json, then: r.then_json });
    setManualOpen(true);
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Stack gap={0}>
          <Title order={4}>Rules & automation</Title>
          <Text size="xs" c="dimmed">
            Save patterns of "from X always do Y." AI suggests rules from your indexed mail —
            you approve through a Safety Review before any rule is active. 24h undo on every activation.
          </Text>
        </Stack>
        <Group gap="xs">
          <Button
            size="xs"
            leftSection={<IconPlus {...ICON_PROPS} />}
            onClick={() => { setEditing(null); setManualOpen(true); }}
          >
            New rule
          </Button>
          <Button size="xs" variant="default" onClick={load} loading={busy}>
            Refresh
          </Button>
        </Group>
      </Group>

      {/* Phase 3: Recent activations — undo any rule activation within 24h */}
      {recentActivations && recentActivations.activations.length > 0 && (
        <Card padding="md" radius="md" withBorder
          style={{ borderLeft: '4px solid var(--mantine-color-orange-6)' }}>
          <Group gap="xs" mb="xs">
            <IconReload {...ICON_PROPS} />
            <Text fw={600} size="sm">Recent activations (24h undo window)</Text>
            <Badge size="xs" color="orange">{recentActivations.activations.length}</Badge>
          </Group>
          <Stack gap={4}>
            {recentActivations.activations.slice(0, 5).map((a) => (
              <Paper key={a.undo_token} p="xs" withBorder>
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500} lineClamp={1}>
                      {a.rule_count === 1 ? a.rule_names[0] : `${a.rule_count} rules`}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Activated {formatRelative(a.activated_at)}
                      {a.rule_count > 1 && ` · ${a.rule_names.slice(0, 2).join(', ')}…`}
                    </Text>
                  </Stack>
                  <Button size="compact-xs" color="orange" variant="light" onClick={() => undoActivation(a.undo_token)}>
                    Undo
                  </Button>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Card>
      )}

      {/* Suggestions */}
      {suggestions && suggestions.suggestions.length > 0 && (
        <Card padding="md" radius="md" withBorder style={{ borderLeft: '4px solid var(--mantine-color-violet-6)' }}>
          <Group gap="xs" mb="xs">
            <IconSparkles {...ICON_PROPS} />
            <Text fw={600}>AI suggestions</Text>
            <Badge color="violet">{suggestions.suggestions.length}</Badge>
          </Group>
          <Stack gap="xs">
            {suggestions.suggestions.map((s) => (
              <Paper key={s.id} p="xs" withBorder>
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm">{s.reason}</Text>
                    <Text size="xs" c="dimmed" ff="monospace">
                      WHEN {JSON.stringify(s.when)} → THEN {JSON.stringify(s.then)}
                    </Text>
                  </Stack>
                  <Group gap={4} style={{ flexShrink: 0 }}>
                    <Badge size="xs" variant="light">
                      {Math.round(s.confidence * 100)}% confidence
                    </Badge>
                    <Button size="compact-xs" onClick={() => approveSuggestion(s)}>
                      Create rule
                    </Button>
                    <Tooltip label="Dismiss this suggestion">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="gray"
                        onClick={() => dismissSuggestion(s.id)}
                      >
                        <IconX {...ICON_PROPS} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Card>
      )}

      {/* Active rules */}
      <Card padding="md" radius="md" withBorder>
        <Text fw={600} size="sm" mb="xs">
          Active rules
        </Text>
        {!rules || rules.rules.length === 0 ? (
          <Text c="dimmed" size="sm">
            No rules yet. Approve suggestions above or wait for more patterns.
          </Text>
        ) : (
          <Stack gap={2}>
            {rules.rules.map((r) => (
              <Paper key={r.id} p="xs" withBorder>
                <Group justify="space-between" wrap="nowrap">
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500}>
                      {r.name}
                    </Text>
                    <Text size="xs" c="dimmed" ff="monospace" lineClamp={1}>
                      {r.when_json} → {r.then_json}
                    </Text>
                  </Stack>
                  <Group gap={4}>
                    <Switch
                      size="sm"
                      checked={!!r.enabled}
                      onChange={(e) => toggleRule(r.id, e.currentTarget.checked)}
                    />
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      onClick={() => startEdit(r)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="red"
                      onClick={() => deleteRule(r.id)}
                    >
                      Delete
                    </Button>
                  </Group>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}
      </Card>

      <ManualRuleModal
        opened={manualOpen}
        editing={editing}
        onClose={() => { setManualOpen(false); setEditing(null); }}
        onSave={saveManualRule}
      />

      <RuleSafetyReviewModal
        opened={!!safetyReviewFor}
        payload={safetyReviewFor}
        onClose={() => setSafetyReviewFor(null)}
        onConfirm={async () => { if (safetyReviewFor) await safetyReviewFor.onConfirm(); }}
      />

      <Alert color="blue" variant="light">
        <Text size="xs">
          Rules are stored and surfaced for your review — they don't yet auto-execute on incoming
          mail. The next round wires the rule engine into the live indexing pipeline so they fire
          automatically with full audit logging.
        </Text>
      </Alert>
    </Stack>
  );
}

export function ManualRuleModal({
  opened,
  editing,
  onClose,
  onSave,
}: {
  opened: boolean;
  editing: { id: string; name: string; when: string; then: string } | null;
  onClose: () => void;
  onSave: (name: string, whenJson: string, thenJson: string) => Promise<void>;
}): React.JSX.Element {
  const [name, setName] = useState('');
  const [whenJson, setWhenJson] = useState('{\n  "sender_contains": ""\n}');
  const [thenJson, setThenJson] = useState('{\n  "action": "tag",\n  "tag": ""\n}');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setName(editing.name);
      try { setWhenJson(JSON.stringify(JSON.parse(editing.when), null, 2)); } catch { setWhenJson(editing.when); }
      try { setThenJson(JSON.stringify(JSON.parse(editing.then), null, 2)); } catch { setThenJson(editing.then); }
    } else if (opened) {
      setName('');
      setWhenJson('{\n  "sender_contains": ""\n}');
      setThenJson('{\n  "action": "tag",\n  "tag": ""\n}');
    }
  }, [editing, opened]);

  return (
    <Modal opened={opened} onClose={onClose} title={editing ? 'Edit rule' : 'Create rule'} size="lg">
      <Stack gap="sm">
        <TextInput
          label="Rule name"
          placeholder="e.g. Auto-archive newsletters from acme"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <Textarea
          label="WHEN (matcher JSON)"
          description="Keys: sender_contains, subject_contains, body_contains, domain, tier (P0|P1|P2|P3|FYI), older_than_days"
          autosize minRows={4} maxRows={10}
          value={whenJson}
          onChange={(e) => setWhenJson(e.currentTarget.value)}
          styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
        />
        <Textarea
          label="THEN (action JSON)"
          description='Keys: action ("archive"|"delete"|"move"|"tag"|"priority"), tag, priority (P0..P4|FYI), folder'
          autosize minRows={3} maxRows={8}
          value={thenJson}
          onChange={(e) => setThenJson(e.currentTarget.value)}
          styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" size="xs" onClick={onClose}>Cancel</Button>
          <Button
            size="xs"
            loading={saving}
            disabled={name.trim().length === 0}
            onClick={async () => {
              setSaving(true);
              try { await onSave(name.trim(), whenJson, thenJson); } finally { setSaving(false); }
            }}
          >
            {editing ? 'Save changes' : 'Create rule'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// Phase 3: Safety Review modal — every rule activation flows through here.
// Shows historical match count, risky-term scan, sample matches, false-positive
// risk score, suggested exceptions. Activation writes a 24h undo token.
export function RuleSafetyReviewModal({
  opened,
  payload,
  onClose,
  onConfirm,
}: {
  opened: boolean;
  payload: {
    when: Record<string, unknown>;
    then: Record<string, unknown>;
    onConfirm: () => Promise<void>;
  } | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}): React.JSX.Element {
  type ReviewData = Awaited<ReturnType<typeof api.addinRuleSafetyReview>>;
  const [data, setData] = useState<ReviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [activating, setActivating] = useState(false);
  const [ackHigh, setAckHigh] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: payload changes drive load
  useEffect(() => {
    if (!opened || !payload) return;
    setData(null);
    setAckHigh(false);
    setLoading(true);
    api
      .addinRuleSafetyReview({ when: payload.when, then: payload.then, window_days: 30 })
      .then(setData)
      .catch((e) => {
        notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Safety review failed' });
        onClose();
      })
      .finally(() => setLoading(false));
  }, [opened, payload]);

  const riskColor = data?.false_positive_risk === 'high' ? 'red' : data?.false_positive_risk === 'medium' ? 'orange' : 'green';
  const blockActivate = !data || (data.false_positive_risk === 'high' && !ackHigh);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconShield {...ICON_PROPS} />
          <Text fw={600}>Safety Review — before activating</Text>
        </Group>
      }
      size="xl"
    >
      <Stack gap="sm">
        {/* Rule summary */}
        <Card padding="sm" radius="md" withBorder>
          <Text size="xs" c="dimmed" mb={4}>This rule:</Text>
          <Text size="sm" ff="monospace">
            WHEN {JSON.stringify(payload?.when ?? {})}
          </Text>
          <Text size="sm" ff="monospace">
            THEN {JSON.stringify(payload?.then ?? {})}
          </Text>
        </Card>

        {loading && (
          <Center p="lg"><Loader size="sm" /></Center>
        )}

        {data && (
          <>
            {/* Risk badge + headline counts */}
            <Group gap="xs" wrap="wrap">
              <Badge color={riskColor} size="lg" leftSection={data.false_positive_risk === 'high' ? <IconAlertTriangle size={12} /> : <IconCheck size={12} />}>
                {data.false_positive_risk.toUpperCase()} false-positive risk
              </Badge>
              <Badge size="lg" variant="light">
                {data.historical_match_count} match{data.historical_match_count === 1 ? '' : 'es'} in last {data.window_days}d
              </Badge>
              {data.vip_match_count > 0 && (
                <Badge size="lg" color="red" variant="light">
                  {data.vip_match_count} from VIPs
                </Badge>
              )}
              {data.critical_tier_match_count > 0 && (
                <Badge size="lg" color="red" variant="light">
                  {data.critical_tier_match_count} P0/P1
                </Badge>
              )}
              {data.replied_to_sender_count > 0 && (
                <Badge size="lg" color="orange" variant="light">
                  You replied to {data.replied_to_sender_count} sender{data.replied_to_sender_count === 1 ? '' : 's'}
                </Badge>
              )}
            </Group>

            {/* Risky terms */}
            {data.risky_terms_found.length > 0 && (
              <Alert color="orange" variant="light" icon={<IconAlertTriangle {...ICON_PROPS} />}>
                <Text size="sm" fw={500} mb={4}>Risky signals detected in matches:</Text>
                <Group gap={4}>
                  {data.risky_terms_found.map((t) => (
                    <Badge key={t} color="orange" variant="filled" size="sm">{t}</Badge>
                  ))}
                </Group>
              </Alert>
            )}

            {/* Suggested exceptions */}
            {data.suggested_exceptions.length > 0 && (
              <Card padding="sm" radius="md" withBorder style={{ borderLeft: '4px solid var(--mantine-color-blue-6)' }}>
                <Text size="sm" fw={600} mb={4}>Suggested exceptions</Text>
                <Stack gap={4}>
                  {data.suggested_exceptions.map((s, i) => (
                    <Group key={`${s.kind}-${s.value}-${i}`} gap="xs" wrap="nowrap">
                      <Badge size="xs" variant="dot">{s.kind}</Badge>
                      <Text size="sm" ff="monospace" style={{ flex: 1 }}>{s.value}</Text>
                      <Text size="xs" c="dimmed">{s.reason}</Text>
                    </Group>
                  ))}
                </Stack>
                <Text size="xs" c="dimmed" mt={4}>
                  Cancel and add these to your WHEN matcher to narrow scope.
                </Text>
              </Card>
            )}

            {/* Sample matches */}
            {data.sample_matches.length > 0 && (
              <Card padding="sm" radius="md" withBorder>
                <Text size="sm" fw={600} mb={4}>
                  Sample of what would be affected ({data.sample_matches.length} of {data.historical_match_count})
                </Text>
                <ScrollArea h={200}>
                  <Stack gap={2}>
                    {data.sample_matches.map((m) => (
                      <Paper key={m.message_id} p={4} withBorder>
                        <Group gap="xs" wrap="nowrap">
                          {m.classification_tier && (
                            <Badge size="xs" color={m.classification_tier === 'P0' ? 'red' : m.classification_tier === 'P1' ? 'orange' : 'gray'}>
                              {m.classification_tier}
                            </Badge>
                          )}
                          <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                            <Text size="xs" fw={500} lineClamp={1}>{m.subject ?? '(no subject)'}</Text>
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {m.from_addr} · {formatRelative(m.received_at)}
                            </Text>
                          </Stack>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </ScrollArea>
              </Card>
            )}

            {data.historical_match_count === 0 && (
              <Alert color="blue" variant="light">
                <Text size="sm">No historical matches in last {data.window_days} days — rule will only act on future mail. Safe to activate.</Text>
              </Alert>
            )}

            {data.false_positive_risk === 'high' && (
              <Alert color="red" variant="filled">
                <Stack gap={6}>
                  <Text size="sm" fw={600}>HIGH risk — this rule could affect critical mail.</Text>
                  <Switch
                    checked={ackHigh}
                    onChange={(e) => setAckHigh(e.currentTarget.checked)}
                    label="I've reviewed the matches above and accept the risk"
                    color="red"
                  />
                </Stack>
              </Alert>
            )}
          </>
        )}

        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose} disabled={activating}>
            Cancel
          </Button>
          <Button
            color={riskColor}
            loading={activating}
            disabled={blockActivate}
            leftSection={<IconShield {...ICON_PROPS} />}
            onClick={async () => {
              setActivating(true);
              try {
                await onConfirm();
                onClose();
              } catch (e) {
                notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
              } finally {
                setActivating(false);
              }
            }}
          >
            Activate (24h undo)
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function AuditPanel(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinAuditLog>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionFilter, setActionFilter] = useState<string | null>(null);
  const [textFilter, setTextFilter] = useState('');
  // sinceDays: 0 = all time. Set by quick chips and persisted in saved presets.
  const [sinceDays, setSinceDays] = useState<number>(0);
  type AuditPreset = { id: string; name: string; action: string | null; text: string };
  const [auditPresets, setAuditPresets] = useState<AuditPreset[]>(() => {
    try { return JSON.parse(localStorage.getItem('addin_audit_presets') ?? '[]') as AuditPreset[]; }
    catch { return []; }
  });
  function persistAuditPresets(next: AuditPreset[]) {
    setAuditPresets(next);
    localStorage.setItem('addin_audit_presets', JSON.stringify(next));
  }

  // Top action types in the loaded entries.
  const actionCounts = new Map<string, number>();
  for (const e of data?.entries ?? []) {
    actionCounts.set(e.action, (actionCounts.get(e.action) ?? 0) + 1);
  }
  const topActions = [...actionCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([type, count]) => ({ type, count }));
  const lowerText = textFilter.trim().toLowerCase();
  const sinceCutoff = sinceDays > 0 ? Date.now() - sinceDays * 86400_000 : 0;
  const filteredEntries = (data?.entries ?? []).filter((e) => {
    if (actionFilter && e.action !== actionFilter) return false;
    if (sinceCutoff > 0 && new Date(e.ts).getTime() < sinceCutoff) return false;
    if (lowerText) {
      const hay = `${e.action} ${e.target_account_id ?? ''} ${e.payload_json ?? ''}`.toLowerCase();
      if (!hay.includes(lowerText)) return false;
    }
    return true;
  });

  async function load() {
    setBusy(true);
    try {
      setData(await api.addinAuditLog());
    } finally {
      setBusy(false);
    }
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount only
  useEffect(() => {
    load();
  }, []);

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Stack gap={0}>
          <Title order={4}>Audit log</Title>
          <Text size="xs" c="dimmed">
            Every action — yours and the AI's — recorded with who, when, and why. Hash-chained
            tamper-evident.
            {data && data.entries.length > 0 && (
              <>{' · '}<b>{filteredEntries.length}</b> of {data.entries.length} shown
              {(filteredEntries.length > 0) && (() => {
                // Rough size estimate: avg JSON length × visible row count.
                const sample = JSON.stringify(filteredEntries[0] ?? {});
                const approxKb = Math.max(1, Math.round((sample.length * filteredEntries.length) / 1024));
                return ` · ~${approxKb} KB if exported`;
              })()}
              </>
            )}
          </Text>
        </Stack>
        <Group gap={4}>
          {/* Two CSV/JSON download buttons reuse the Phase 8 export endpoint
              with bearer auth. Same pattern as the Trust Center card. */}
          <Menu position="bottom-end">
            <Menu.Target>
              <Button size="xs" variant="subtle" leftSection={<IconStar size={12} />}
                rightSection={auditPresets.length > 0 ? <Badge size="xs" variant="light">{auditPresets.length}</Badge> : null}>
                Presets
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item leftSection={<IconPlus size={14} />}
                disabled={!actionFilter && !textFilter.trim()}
                onClick={() => {
                  const name = prompt('Preset name:');
                  if (!name?.trim()) return;
                  const p: AuditPreset = {
                    id: `ap_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    name: name.trim(), action: actionFilter, text: textFilter.trim(),
                  };
                  persistAuditPresets([...auditPresets, p]);
                  notifications.show({ message: `Saved preset "${p.name}".` });
                }}>
                Save current as preset…
              </Menu.Item>
              <Menu.Item leftSection={<IconFileText size={14} />}
                disabled={auditPresets.length === 0}
                onClick={() => {
                  const blob = new Blob([JSON.stringify(auditPresets, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `addin-audit-presets-${new Date().toISOString().slice(0, 10)}.json`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                }}>
                Export presets ({auditPresets.length})
              </Menu.Item>
              <Menu.Item leftSection={<IconArrowBackUp size={14} />}
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = '.json,application/json';
                  input.onchange = () => {
                    const file = input.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      try {
                        const parsed = JSON.parse(String(reader.result)) as AuditPreset[];
                        if (!Array.isArray(parsed)) throw new Error('Expected array');
                        const byName = new Map(auditPresets.map((p) => [p.name, p]));
                        for (const p of parsed) {
                          if (!p?.name) continue;
                          byName.set(p.name, {
                            id: p.id ?? `ap_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                            name: p.name,
                            action: p.action ?? null,
                            text: p.text ?? '',
                          });
                        }
                        persistAuditPresets([...byName.values()]);
                        notifications.show({ color: 'green', message: `Imported ${parsed.length} preset${parsed.length === 1 ? '' : 's'}` });
                      } catch (e) {
                        notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Bad JSON' });
                      }
                    };
                    reader.readAsText(file);
                  };
                  input.click();
                }}>
                Import presets…
              </Menu.Item>
              {auditPresets.length > 0 && <Menu.Divider />}
              {auditPresets.length === 0 && (
                <Menu.Item disabled><Text size="xs" c="dimmed">No saved presets.</Text></Menu.Item>
              )}
              {auditPresets.map((p) => (
                <Menu.Item key={p.id}
                  rightSection={
                    <ActionIcon size="xs" variant="subtle" color="red"
                      onClick={(e) => { e.stopPropagation(); persistAuditPresets(auditPresets.filter((x) => x.id !== p.id)); }}>
                      <IconX size={10} />
                    </ActionIcon>
                  }
                  onClick={() => { setActionFilter(p.action); setTextFilter(p.text); }}>
                  <Stack gap={0}>
                    <Text size="sm" fw={500}>{p.name}</Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {[p.action && `action=${p.action}`, p.text && `q=${p.text}`].filter(Boolean).join(' · ') || 'no filters'}
                    </Text>
                  </Stack>
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
          {(['csv', 'json'] as const).map((fmt) => (
            <Button
              key={fmt}
              size="xs"
              variant="subtle"
              leftSection={<IconFileText size={12} />}
              onClick={() => {
                // Filter-aware export: when text/action filter is active, build
                // the file from filteredEntries (already in-memory). Otherwise
                // fall through to the brain endpoint for the full chain.
                const filtered = !!(actionFilter || textFilter.trim());
                if (filtered && filteredEntries.length > 0) {
                  let body: string;
                  if (fmt === 'csv') {
                    const esc = (s: string | null | undefined) => {
                      if (s == null) return '';
                      const v = String(s);
                      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
                    };
                    body = ['id,ts,action,actor_role,target_account,payload',
                      ...filteredEntries.map((e) => [
                        e.id, e.ts, e.action, e.actor_role_at_time, e.target_account_id, e.payload_json,
                      ].map(esc).join(',')),
                    ].join('\n');
                  } else {
                    body = JSON.stringify({ filtered: { action: actionFilter, text: textFilter }, entries: filteredEntries }, null, 2);
                  }
                  const blob = new Blob([body], { type: fmt === 'csv' ? 'text/csv;charset=utf-8' : 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  const tag = [actionFilter, textFilter.trim() && 'q'].filter(Boolean).join('-');
                  a.download = `audit-${tag || 'filtered'}-${new Date().toISOString().slice(0, 10)}.${fmt}`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                  return;
                }
                const tok = getStoredToken();
                if (!tok) { notifications.show({ color: 'red', message: 'Not authenticated' }); return; }
                fetch(api.addinAuditExportUrl({ format: fmt }), {
                  headers: { authorization: `Bearer ${tok}` },
                })
                  .then(async (res) => {
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `audit-${new Date().toISOString().slice(0, 10)}.${fmt}`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                  })
                  .catch((e) => notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Download failed' }));
              }}
            >
              {fmt.toUpperCase()} ({actionFilter || textFilter.trim() ? filteredEntries.length : 'all'})
            </Button>
          ))}
          <Button size="xs" variant="default" onClick={load} loading={busy}>
            Refresh
          </Button>
        </Group>
      </Group>

      {(() => {
        if (!data || data.entries.length === 0) return null;
        const startToday = new Date(); startToday.setHours(0, 0, 0, 0);
        const today = data.entries.filter((e) => new Date(e.ts).getTime() >= startToday.getTime());
        if (today.length === 0) return null;
        const counts = new Map<string, number>();
        for (const e of today) counts.set(e.action, (counts.get(e.action) ?? 0) + 1);
        const top = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4);
        return (
          <Alert color="blue" variant="light" icon={<IconHistory size={14} />} p={6}>
            <Text size="xs">
              <b>{today.length}</b> action{today.length === 1 ? '' : 's'} today
              {top.length > 0 && ' — '}
              {top.map(([k, n], i) => (
                <span key={k}>{i > 0 && ', '}{n} {k.replace(/_/g, ' ')}</span>
              ))}
            </Text>
          </Alert>
        );
      })()}
      {(data?.entries.length ?? 0) > 0 && (
        <TextInput
          size="xs"
          placeholder="Filter audit entries (action, payload, account)…"
          value={textFilter}
          onChange={(e) => setTextFilter(e.currentTarget.value)}
          rightSection={textFilter ? (
            <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setTextFilter('')}>
              <IconX size={10} />
            </ActionIcon>
          ) : null}
        />
      )}

      {(data?.entries.length ?? 0) > 0 && (
        <Group gap={4} wrap="wrap">
          <Text size="xs" c="dimmed">Quick:</Text>
          <Badge size="xs" variant={sinceDays === 7 ? 'filled' : 'light'} color="grape"
            style={{ cursor: 'pointer' }}
            onClick={() => setSinceDays(sinceDays === 7 ? 0 : 7)}>
            This week
          </Badge>
          <Badge size="xs" variant={sinceDays === 30 ? 'filled' : 'light'} color="grape"
            style={{ cursor: 'pointer' }}
            onClick={() => setSinceDays(sinceDays === 30 ? 0 : 30)}>
            This month
          </Badge>
          <Badge size="xs" variant={actionFilter === 'settings_update' ? 'filled' : 'light'} color="grape"
            style={{ cursor: 'pointer' }}
            onClick={() => setActionFilter(actionFilter === 'settings_update' ? null : 'settings_update')}>
            Settings only
          </Badge>
          <Badge size="xs" variant={actionFilter === 'rule_activated' || actionFilter === 'rule_disabled' ? 'filled' : 'light'} color="grape"
            style={{ cursor: 'pointer' }}
            onClick={() => setActionFilter(actionFilter === 'rule_activated' ? null : 'rule_activated')}>
            Rules only
          </Badge>
          {(sinceDays > 0 || actionFilter || textFilter.trim()) && (
            <Badge size="xs" variant="subtle" color="gray"
              style={{ cursor: 'pointer' }}
              onClick={() => { setSinceDays(0); setActionFilter(null); setTextFilter(''); }}>
              Clear all
            </Badge>
          )}
        </Group>
      )}

      {topActions.length > 1 && (
        <Group gap={4} wrap="wrap">
          <Text size="xs" c="dimmed">Action:</Text>
          <Badge size="xs" variant={actionFilter === null ? 'filled' : 'light'} color="blue"
            style={{ cursor: 'pointer' }} onClick={() => setActionFilter(null)}>
            All {data?.entries.length ?? 0}
          </Badge>
          {topActions.map((a) => (
            <Badge
              key={a.type}
              size="xs"
              variant={actionFilter === a.type ? 'filled' : 'light'}
              color="blue"
              style={{ cursor: 'pointer', maxWidth: 220 }}
              onClick={() => setActionFilter(actionFilter === a.type ? null : a.type)}
            >
              <Text size="xs" lineClamp={1}>{a.type} {a.count}</Text>
            </Badge>
          ))}
        </Group>
      )}

      {busy && !data ? (
        <Center p="xl">
          <Loader />
        </Center>
      ) : !data || data.entries.length === 0 ? (
        <Card padding="xl" radius="md" withBorder>
          <Center>
            <Text c="dimmed">No audit entries yet.</Text>
          </Center>
        </Card>
      ) : filteredEntries.length === 0 ? (
        <Text size="xs" c="dimmed" ta="center" py="md">No entries match this filter.</Text>
      ) : (
        <Card padding="md" radius="md" withBorder>
          <ScrollArea h={520}>
            <Stack gap={4}>
              {(() => {
                // Group entries by Today / Yesterday / Earlier this week / Older.
                const now = new Date();
                const startToday = new Date(now); startToday.setHours(0, 0, 0, 0);
                const startYesterday = new Date(startToday); startYesterday.setDate(startToday.getDate() - 1);
                const startWeek = new Date(startToday); startWeek.setDate(startToday.getDate() - 7);
                function bucket(ts: string): string {
                  const t = new Date(ts).getTime();
                  if (t >= startToday.getTime()) return 'Today';
                  if (t >= startYesterday.getTime()) return 'Yesterday';
                  if (t >= startWeek.getTime()) return 'Earlier this week';
                  return 'Older';
                }
                const blocks = new Map<string, typeof filteredEntries>();
                for (const e of filteredEntries) {
                  const k = bucket(e.ts);
                  const arr = blocks.get(k) ?? [];
                  arr.push(e);
                  blocks.set(k, arr);
                }
                const order = ['Today', 'Yesterday', 'Earlier this week', 'Older'];
                return order.flatMap((label) => {
                  const arr = blocks.get(label);
                  if (!arr || arr.length === 0) return [];
                  return [
                    <Divider key={`d-${label}`} label={`${label} (${arr.length})`} labelPosition="left" />,
                    ...arr.map((e) => {
                let payload: Record<string, unknown> = {};
                try {
                  if (e.payload_json) payload = JSON.parse(e.payload_json);
                } catch {
                  /* ignore */
                }
                return (
                  <AuditEntryRow key={e.id} entry={e} payload={payload} />
                );
                    }),
                  ];
                });
              })()}
            </Stack>
          </ScrollArea>
        </Card>
      )}
    </Stack>
  );
}

// Audit entry row with expand-payload + copy-as-JSON.
export function AuditEntryRow({
  entry,
  payload,
}: {
  entry: { id: string; action: string; actor_user_id?: string; actor_role_at_time: string | null;
           target_account_id: string | null; ts: string; payload_json: string | null };
  payload: Record<string, unknown>;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const hasPayload = Object.keys(payload).length > 0;
  // Time-of-day chip — local timezone hour buckets.
  const hour = new Date(entry.ts).getHours();
  const todLabel = hour < 5 ? 'night' : hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : hour < 22 ? 'evening' : 'night';
  const todColor = todLabel === 'morning' ? 'yellow' : todLabel === 'afternoon' ? 'blue' : todLabel === 'evening' ? 'orange' : 'gray';
  return (
    <Paper p="xs" style={{ borderLeft: '3px solid var(--mantine-color-blue-3)' }}>
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs">
            <Text size="sm" fw={500}>{entry.action}</Text>
            <Badge size="xs" variant="dot" color={todColor}>{todLabel}</Badge>
            <Badge size="xs" variant="light">{entry.actor_role_at_time}</Badge>
            {entry.target_account_id && (
              <Badge size="xs" variant="dot">
                {entry.target_account_id.slice(0, 12)}…
              </Badge>
            )}
            {hasPayload && (
              <Button
                size="compact-xs"
                variant="subtle"
                onClick={() => setOpen((v) => !v)}
              >
                {open ? 'Hide' : 'Expand'}
              </Button>
            )}
            <Tooltip label="Copy entry as JSON">
              <ActionIcon size="xs" variant="subtle" color="gray"
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify({
                    id: entry.id, ts: entry.ts, action: entry.action,
                    actor_role_at_time: entry.actor_role_at_time,
                    target_account_id: entry.target_account_id,
                    payload,
                  }, null, 2))
                    .then(() => notifications.show({ message: 'Entry copied as JSON.' }))
                    .catch(() => notifications.show({ color: 'red', message: 'Copy failed' }));
                }}>
                <IconFileText size={10} />
              </ActionIcon>
            </Tooltip>
          </Group>
          {hasPayload && !open && (
            <Text size="xs" c="dimmed" lineClamp={2} ff="monospace">
              {JSON.stringify(payload).slice(0, 200)}
            </Text>
          )}
          {hasPayload && open && (
            <Code block style={{ fontSize: 11, marginTop: 4, maxHeight: 240, overflowY: 'auto' }}>
              {JSON.stringify(payload, null, 2)}
            </Code>
          )}
        </Stack>
        <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
          {new Date(entry.ts).toLocaleString()}
        </Text>
      </Group>
    </Paper>
  );
}

// =========================================================================
// Round 34 components — Snooze, ReplyComposer, SenderProfileDrawer, CommandBar
// =========================================================================

